import { noop } from '@direct-frontend/stdlib';
import { createApi, createBaseQuery } from '@adv-frontend/modules-query';
import { apiV1Request } from '#shared/api/requests';
import {
    OpenApiCurrentEmailMethod,
    type OpenApiCurrentEmailResponseSuccess,
    type OpenApiCurrentEmailSearchParams,
    OpenApiCurrentEmailUrl,
    OpenApiCustomDomainEnabledMethod,
    type OpenApiCustomDomainEnabledRequest,
    type OpenApiCustomDomainEnabledResponseSuccess,
    OpenApiCustomDomainEnabledUrl,
    OpenApiDeleteDomainRecordMethod,
    type OpenApiDeleteDomainRecordRequest,
    type OpenApiDeleteDomainRecordResponseSuccess,
    OpenApiDeleteDomainRecordUrl,
    OpenApiEmailStatusMethod,
    type OpenApiEmailStatusResponseSuccess,
    type OpenApiEmailStatusSearchParams,
    OpenApiEmailStatusUrl,
    type OpenApiGetCurrentDomainRequest,
    type OpenApiGetCurrentDomainResponseSuccess,
    type OpenApiGetDNSRecordsResponseSuccess,
    type OpenApiGetDNSRecordsSearchParams,
    type OpenApiGetDomainStatusResponseSuccess,
    type OpenApiGetDomainStatusSearchParams,
    OpenApiGetMailDomainDKIMRecordsMethod,
    type OpenApiGetMailDomainDKIMRecordsResponseSuccess,
    OpenApiGetMailDomainDKIMRecordsUrl,
    type OpenApiInitCreateDomainRequest,
    type OpenApiInitCreateDomainResponseSuccess,
    OpenApiInitDeleteDomainMethod,
    type OpenApiInitDeleteDomainRequest,
    type OpenApiInitDeleteDomainResponseSuccess,
    OpenApiInitDeleteDomainUrl,
    type OpenApiListDomainRecordsResponseSuccess,
    type OpenApiListDomainRecordsSearchParams,
    OpenApiListDomainRecordsUrl,
    type OpenApiPublishDomainResponseSuccess,
    type OpenApiPublishDomainSearchParams,
    OpenApiSendTestMessageMethod,
    type OpenApiSendTestMessageRequest,
    type OpenApiSendTestMessageResponseSuccess,
    OpenApiSendTestMessageUrl,
    OpenApiSetDomainRecordMethod,
    type OpenApiSetDomainRecordRequest,
    type OpenApiSetDomainRecordResponseSuccess,
    OpenApiSetDomainRecordUrl,
    OpenApiSetEmailMethod,
    type OpenApiSetEmailRequest,
    type OpenApiSetEmailResponseSuccess,
    OpenApiSetEmailUrl,
    OpenApiValidateDelegatedDomainMethod,
    type OpenApiValidateDelegatedDomainRequest,
    type OpenApiValidateDelegatedDomainResponseSuccess,
    OpenApiValidateDelegatedDomainUrl,
} from '#shared/api/schema';
import { getLocalExperimentalFlag } from '#shared/lib/flags';

const DomainTag = 'Domain' as const;

const domainRecordsTag = { type: DomainTag, id: 'domainRecords' };
const emailStatusTag = { type: DomainTag, id: 'emailStatus' };
const currentEmailTag = { type: DomainTag, id: 'currentEmail' };

const api = createApi({
    name: 'entities/domain/api/api',
    tagTypes: [DomainTag],
    baseQuery: createBaseQuery(apiV1Request),
    endpoints: (builder) => {
        return {
            getDomainInfo: builder.query<OpenApiGetCurrentDomainResponseSuccess, OpenApiGetCurrentDomainRequest>({
                query: () => {
                    return {
                        url: '/awacs/current-domain',
                    };
                },
            }),
            getDomainStatus: builder.query<OpenApiGetDomainStatusResponseSuccess, OpenApiGetDomainStatusSearchParams>({
                query: ({ domain, operation = 'create' }) => {
                    return {
                        url: '/awacs/domain',
                        params: { domain, operation },
                    };
                },
                transformResponse: (response: OpenApiGetDomainStatusResponseSuccess) => {
                    const statusOverride = getLocalExperimentalFlag({
                        name: 'override_domain_status',
                    }) as OpenApiGetDomainStatusResponseSuccess['status'];

                    return {
                        ...response,
                        status: statusOverride || response.status,
                    };
                },
            }),
            generateDomainDnsRecords: builder.query<
                OpenApiGetDNSRecordsResponseSuccess,
                OpenApiGetDNSRecordsSearchParams
            >({
                query: ({ domain }) => {
                    return {
                        url: `/awacs/records?domain=${domain}`,
                        method: 'GET',
                    };
                },
            }),
            generateDomainDnsPublishRecords: builder.query<
                OpenApiPublishDomainResponseSuccess,
                OpenApiPublishDomainSearchParams
            >({
                query: ({ domain }) => {
                    return {
                        url: `/awacs/publish?domain=${domain}`,
                        method: 'GET',
                    };
                },
            }),
            createDomain: builder.mutation<OpenApiInitCreateDomainResponseSuccess, OpenApiInitCreateDomainRequest>({
                query: (payload) => {
                    return {
                        url: `/awacs/domain`,
                        method: 'POST',
                        body: payload,
                    };
                },
                async onQueryStarted(arg, { queryFulfilled }) {
                    const queryFulfilledResult = await queryFulfilled.catch(noop);

                    if (queryFulfilledResult === undefined) {
                        return;
                    }

                    const { data } = queryFulfilledResult;

                    if (arg.is_delegated) {
                        await api.endpoints.getDomainStatus.upsertQueryData(
                            { domain: data.domain },
                            // Оптимистично проставляем следующий шаг
                            { status: 'VALIDATION_IN_PROGRESS' }
                        );
                    }

                    api.endpoints.getDomainInfo.updateQueryData(undefined, () => data);
                },
            }),
            validateDelegatedDomain: builder.query<
                OpenApiValidateDelegatedDomainResponseSuccess,
                OpenApiValidateDelegatedDomainRequest
            >({
                query: ({ domain }) => {
                    return {
                        url: OpenApiValidateDelegatedDomainUrl,
                        method: OpenApiValidateDelegatedDomainMethod,
                        body: { domain },
                    };
                },
            }),
            getDomainRecords: builder.query<
                OpenApiListDomainRecordsResponseSuccess['records'],
                OpenApiListDomainRecordsSearchParams
            >({
                query: () => {
                    return {
                        url: OpenApiListDomainRecordsUrl,
                    };
                },
                providesTags: [domainRecordsTag],
            }),
            upsertDomainRecord: builder.mutation<OpenApiSetDomainRecordResponseSuccess, OpenApiSetDomainRecordRequest>({
                query: (payload) => {
                    return {
                        url: OpenApiSetDomainRecordUrl,
                        method: OpenApiSetDomainRecordMethod,
                        body: payload,
                    };
                },
                invalidatesTags: [domainRecordsTag],
            }),
            unlinkDomain: builder.mutation<OpenApiInitDeleteDomainResponseSuccess, OpenApiInitDeleteDomainRequest>({
                query: (payload) => {
                    return {
                        url: OpenApiInitDeleteDomainUrl,
                        method: OpenApiInitDeleteDomainMethod,
                        body: payload,
                    };
                },
            }),
            deleteDomainRecord: builder.mutation<
                OpenApiDeleteDomainRecordResponseSuccess,
                OpenApiDeleteDomainRecordRequest
            >({
                query: (payload) => {
                    return {
                        url: OpenApiDeleteDomainRecordUrl,
                        method: OpenApiDeleteDomainRecordMethod,
                        body: payload,
                    };
                },
                invalidatesTags: [domainRecordsTag],
            }),
            getEmailStatus: builder.query<OpenApiEmailStatusResponseSuccess, OpenApiEmailStatusSearchParams>({
                query: (payload) => {
                    return {
                        url: OpenApiEmailStatusUrl,
                        method: OpenApiEmailStatusMethod,
                        params: payload,
                    };
                },
                providesTags: [emailStatusTag],
            }),
            sendTestMessage: builder.mutation<OpenApiSendTestMessageResponseSuccess, OpenApiSendTestMessageRequest>({
                query: (payload) => {
                    return {
                        url: OpenApiSendTestMessageUrl,
                        method: OpenApiSendTestMessageMethod,
                        body: payload,
                    };
                },
            }),
            setEmail: builder.mutation<OpenApiSetEmailResponseSuccess, OpenApiSetEmailRequest>({
                query: (payload) => {
                    return {
                        url: OpenApiSetEmailUrl,
                        method: OpenApiSetEmailMethod,
                        body: payload,
                    };
                },
                invalidatesTags: [emailStatusTag, currentEmailTag],
            }),
            getCurrentEmail: builder.query<OpenApiCurrentEmailResponseSuccess, OpenApiCurrentEmailSearchParams>({
                query: (params) => {
                    return {
                        url: OpenApiCurrentEmailUrl,
                        method: OpenApiCurrentEmailMethod,
                        params,
                    };
                },
                providesTags: [currentEmailTag],
            }),
            getCustomDomainEnabled: builder.query<
                OpenApiCustomDomainEnabledResponseSuccess,
                OpenApiCustomDomainEnabledRequest
            >({
                query: () => {
                    return {
                        url: OpenApiCustomDomainEnabledUrl,
                        method: OpenApiCustomDomainEnabledMethod,
                    };
                },
            }),
            getMailDomainDkimRecords: builder.query<OpenApiGetMailDomainDKIMRecordsResponseSuccess, void>({
                query: () => {
                    return {
                        url: OpenApiGetMailDomainDKIMRecordsUrl,
                        method: OpenApiGetMailDomainDKIMRecordsMethod,
                    };
                },
            }),
        };
    },
});

export const upsertDomainStatus = api.endpoints.getDomainStatus.upsertQueryData;
export const getDomainStatus = api.endpoints.getDomainStatus.query;
export const useDomainInfo = api.useGetDomainInfoQuery;
export const useLazyValidateDelegatedDomain = api.useLazyValidateDelegatedDomainQuery;
export const useDomainStatus = api.useGetDomainStatusQuery;
export const useLazyDomainDnsRecords = api.useLazyGenerateDomainDnsRecordsQuery;
export const generateDomainDnsPublishRecords = api.endpoints.generateDomainDnsPublishRecords.query;
export const createDomain = api.endpoints.createDomain.mutate;
export const useDomainRecords = api.useGetDomainRecordsQuery;
export const upsertDomainRecord = api.endpoints.upsertDomainRecord.mutate;
export const deleteDomainRecord = api.endpoints.deleteDomainRecord.mutate;
export const unlinkDomain = api.endpoints.unlinkDomain.mutate;
export const useEmailDomainStatus = api.useGetEmailStatusQuery;
export const sendTestDomainMessage = api.endpoints.sendTestMessage.mutate;
export const setEmail = api.endpoints.setEmail.mutate;
export const useCurrentEmail = api.useGetCurrentEmailQuery;
export const useCustomDomainEnabled = api.useGetCustomDomainEnabledQuery;
export const useMailDomainDkimRecords = api.useGetMailDomainDkimRecordsQuery;
